/**
 * Configuración de Estilos para Red Urbana (Canonical Street Network)
 *
 * @module core/spatial/config
 * @version 1.0
 * @created 2026-03-02
 *
 * @description
 * Define la configuración de estilos visuales para la Red Vial Canónica.
 * Los valores por defecto pueden ser sobrescritos por el usuario mediante
 * el NetworkStylePanel.
 *
 * Estructura:
 * - edges: Estilos para líneas (segmentos de calle)
 * - nodes: Estilos para nodos (intersecciones)
 *
 * @example
 * ```typescript
 * import { DEFAULT_NETWORK_STYLE } from './network-style.config';
 *
 * const customStyle = {
 *   ...DEFAULT_NETWORK_STYLE,
 *   edges: {
 *     ...DEFAULT_NETWORK_STYLE.edges,
 *     color: '#FF0000', // Líneas rojas
 *   }
 * };
 * ```
 */

/**
 * Configuración de estilos para edges (segmentos de calle)
 */
export interface EdgeStyleConfig {
  /** Color de la línea (CSS hex color) */
  color: string;
  /** Ancho de la línea en píxeles */
  width: number;
  /** Opacidad de la línea (0-1) */
  opacity: number;
  /** Estilo de terminación de línea */
  cap: 'butt' | 'round' | 'square';
  /** Estilo de unión de líneas */
  join: 'bevel' | 'round' | 'miter';
}

/**
 * Configuración de estilos para nodes (nodos de intersección)
 */
export interface NodeStyleConfig {
  /** Radio del círculo en píxeles */
  radius: number;
  /** Color de relleno del círculo (CSS hex color) */
  fillColor: string;
  /** Color del borde del círculo (CSS hex color) */
  strokeColor: string;
  /** Ancho del borde en píxeles */
  strokeWidth: number;
  /** Opacidad del círculo (0-1) */
  opacity: number;
}

/**
 * Configuración completa de estilos para la Red Urbana
 */
export interface NetworkStyleConfig {
  /** Estilos para edges (segmentos de calle) */
  edges: EdgeStyleConfig;
  /** Estilos para nodes (intersecciones) */
  nodes: NodeStyleConfig;
}

/**
 * Estilos por defecto para la Red Urbana
 *
 * Visualización convencional:
 * - Líneas blancas de 2px que van de centro a centro de nodos
 * - Nodos blancos con relleno sólido de 6px de radio
 * - El relleno del nodo tapa visualmente las puntas de las líneas
 */
export const DEFAULT_NETWORK_STYLE: NetworkStyleConfig = {
  edges: {
    color: '#FFFFFF',
    width: 2,
    opacity: 1,
    cap: 'round',
    join: 'round',
  },
  nodes: {
    radius: 6,
    fillColor: '#FFFFFF',
    strokeColor: '#FFFFFF',
    strokeWidth: 2,
    opacity: 1,
  },
};

/**
 * Presets de estilos predefinidos
 */
export const NETWORK_STYLE_PRESETS: Record<string, NetworkStyleConfig> = {
  /**
   * Preset por defecto (blanco sobre oscuro)
   */
  default: DEFAULT_NETWORK_STYLE,

  /**
   * Preset de alto contraste (para accesibilidad)
   */
  highContrast: {
    edges: {
      color: '#FFFF00', // Amarillo brillante
      width: 3,
      opacity: 1,
      cap: 'round',
      join: 'round',
    },
    nodes: {
      radius: 8,
      fillColor: '#FFFF00',
      strokeColor: '#000000',
      strokeWidth: 3,
      opacity: 1,
    },
  },

  /**
   * Preset sutil (para no distraer del mapa base)
   */
  subtle: {
    edges: {
      color: '#CCCCCC',
      width: 1,
      opacity: 0.6,
      cap: 'round',
      join: 'round',
    },
    nodes: {
      radius: 4,
      fillColor: '#CCCCCC',
      strokeColor: '#CCCCCC',
      strokeWidth: 1,
      opacity: 0.6,
    },
  },

  /**
   * Preset de énfasis (para presentaciones)
   */
  emphasis: {
    edges: {
      color: '#2FA4B5', // Cyan de WESTEER
      width: 4,
      opacity: 1,
      cap: 'round',
      join: 'round',
    },
    nodes: {
      radius: 10,
      fillColor: '#2FA4B5',
      strokeColor: '#FFFFFF',
      strokeWidth: 2,
      opacity: 1,
    },
  },
};

/**
 * Valida que una configuración de estilo sea válida
 *
 * @param config - Configuración a validar
 * @returns true si es válida, false en caso contrario
 */
export function isValidNetworkStyle(config: NetworkStyleConfig): boolean {
  // Validar edges
  if (
    typeof config.edges.width !== 'number' ||
    config.edges.width < 0 ||
    config.edges.width > 20
  ) {
    return false;
  }

  if (
    typeof config.edges.opacity !== 'number' ||
    config.edges.opacity < 0 ||
    config.edges.opacity > 1
  ) {
    return false;
  }

  // Validar nodes
  if (
    typeof config.nodes.radius !== 'number' ||
    config.nodes.radius < 0 ||
    config.nodes.radius > 50
  ) {
    return false;
  }

  if (
    typeof config.nodes.opacity !== 'number' ||
    config.nodes.opacity < 0 ||
    config.nodes.opacity > 1
  ) {
    return false;
  }

  return true;
}
